// DEM-1870: merge HealthPage (system checks) + MCHealthPage (operational view)
// em uma página única com tabs. Ambas capacidades preservadas — system
// checks (db/pgvector/latency, readiness/liveness) ficam na tab "Sistema",
// operational view (Gateway/Database/Workspace/LLM Usage) na tab "Operacional".
// /mc/health redireciona via Navigate (App.tsx) — mesmo padrão de /mc/tasks → /kanban.
import { useEffect, useState, useCallback } from 'react';
import {
  api,
  type HarnessAssetInventory,
  type HarnessErrorClassification,
  type HarnessErrorsResponse,
  type HarnessCurrentHealth,
  type HarnessHealthDay,
  type HarnessHealthResponse,
  type HarnessSubsystem,
} from '../lib/api';
import {
  Activity,
  AlertTriangle,
  Boxes,
  Cpu,
  Database,
  HardDrive,
  HeartPulse,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs } from '@/components/ui/Tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

interface HealthData {
  status: string;
  latency_ms: number;
  checks: { database: string; pgvector: string };
}

type StatusBadgeVariant = 'success' | 'warning' | 'danger';
type HealthTab = 'system' | 'operational' | 'harness';
type HarnessErrorPageSize = 10 | 25 | 50 | 100;

function getStatusBadgeVariant(value: string): StatusBadgeVariant {
  if (value === 'ok') return 'success';
  if (value === 'degraded') return 'warning';
  return 'danger';
}

function getLatencyVariant(ms: number): 'success' | 'warning' | 'danger' {
  if (ms < 100) return 'success';
  if (ms < 300) return 'warning';
  return 'danger';
}

/* ─── Operational sub-components (migrados de MCHealthPage) ─────────────── */

function StatusDot({ status }: { status: 'online' | 'error' | 'idle' }) {
  return (
    <div
      className={cn(
        'h-2 w-2 rounded-full shrink-0',
        status === 'online' && 'bg-raios-success',
        status === 'error' && 'bg-raios-danger',
        status === 'idle' && 'bg-raios-elevated',
      )}
    />
  );
}

function latencyLabel(ms: number | null): string {
  if (ms == null) return '-';
  return `${ms}ms`;
}

interface HealthCardProps {
  icon: React.ElementType;
  title: string;
  status: 'online' | 'offline' | 'checking';
  children: React.ReactNode;
}

function HealthCard({ icon: Icon, title, status, children }: HealthCardProps) {
  const dotStatus = status === 'online' ? 'online' : status === 'offline' ? 'error' : 'idle';
  return (
    <div className="ghost-card p-4 space-y-3">
      <div className="flex items-center gap-2.5">
        <div className="rounded-md bg-raios-elevated/60 p-1.5">
          <Icon className="h-4 w-4 text-raios-muted" strokeWidth={1.8} />
        </div>
        <span className="text-sm font-semibold text-raios-text">{title}</span>
        <div className="ml-auto flex items-center gap-1.5">
          <StatusDot status={dotStatus} />
          <span className="text-xs text-raios-text-secondary capitalize">
            {status === 'online' ? 'Online' : status === 'offline' ? 'Offline' : 'Verificando'}
          </span>
        </div>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-raios-muted">{label}</span>
      <span className="font-mono text-raios-text">{value}</span>
    </div>
  );
}

const SUBSYSTEM_LABELS: Record<HarnessSubsystem, string> = {
  database: 'Banco de dados',
  engine: 'Engine',
  llm: 'LLM',
  companions: 'Companions',
  agents: 'Agentes',
  skills: 'Skills',
  squads: 'Squads',
  loops: 'Loops',
  storage: 'Storage',
  connectors: 'Conectores',
  scheduler: 'Scheduler',
  tools: 'Tools',
};

const SUBSYSTEMS = Object.keys(SUBSYSTEM_LABELS) as HarnessSubsystem[];

function healthCellClass(status: HarnessHealthDay['status']): string {
  if (status === 'operational') return 'bg-raios-success';
  if (status === 'degraded') return 'bg-raios-warning';
  if (status === 'outage') return 'bg-raios-danger';
  return 'bg-raios-elevated';
}

function formatRatioAsPercent(value: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const ERROR_CLASSIFICATION_LABELS: Record<HarnessErrorClassification, string> = {
  policy_block: 'Bloqueio de política',
  idempotency: 'Idempotência',
  billing: 'Billing',
  accounts: 'Contas',
  delivery: 'Entrega',
  content_limits: 'Limite de conteúdo',
  workspace: 'Workspace',
  external_content: 'Conteúdo externo',
  external_resource: 'Recurso externo',
  persistence: 'Persistência',
  media_validation: 'Validação de mídia',
  orchestration: 'Orquestração',
  database: 'Banco de dados',
  runtime: 'Runtime',
  connectors: 'Conectores',
  scheduler: 'Scheduler',
  auth: 'Autenticação',
  stuck: 'Stuck',
  timeout: 'Timeout',
  rate_limit: 'Rate limit',
  network: 'Rede',
  storage: 'Storage',
  contract: 'Contrato',
  configuration: 'Configuração',
  provider: 'Provider',
  business_logic: 'Regra de negócio',
  other: 'Outros · classificar',
};

function formatExactCount(value: number | string): string {
  try {
    return BigInt(value).toLocaleString('pt-BR');
  } catch {
    return String(value);
  }
}

function HarnessErrorsPanel({
  data,
  error,
  loading,
  loadingMore,
  loadingEvents,
  onLoadMore,
  onLoadEvents,
  summaryPage,
  summaryPageSize,
  onSummaryPageChange,
  onSummaryPageSizeChange,
}: {
  data: HarnessErrorsResponse | null;
  error: string;
  loading: boolean;
  loadingMore: boolean;
  loadingEvents: boolean;
  onLoadMore: () => void;
  onLoadEvents: () => void;
  summaryPage: number;
  summaryPageSize: HarnessErrorPageSize;
  onSummaryPageChange: (page: number) => void;
  onSummaryPageSizeChange: (pageSize: HarnessErrorPageSize) => void;
}) {
  return (
    <Card className="p-5">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-raios-text">Erros nas últimas 48 horas</h2>
          <p className="text-xs text-raios-muted">
            Totais completos no banco, com o resumo carregado por página.
          </p>
        </div>
        {data && (
          <div className="flex flex-wrap gap-2">
            <Badge variant={data.total_occurrences === '0' ? 'success' : 'danger'}>
              {formatExactCount(data.total_occurrences)} ocorrências
            </Badge>
            <Badge variant={data.other_occurrences === '0' ? 'outline' : 'warning'}>
              {formatExactCount(data.other_occurrences)} em Outros
            </Badge>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-raios-danger/30 bg-raios-danger/5 p-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-raios-danger" />
          <span className="text-xs text-raios-danger">{error}</span>
        </div>
      )}

      {!data && loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : data && data.summary.length === 0 ? (
        <div className="rounded-lg bg-raios-success/5 p-4 text-xs text-raios-success">
          Nenhum erro registrado nesta janela.
        </div>
      ) : data ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead>
                <tr className="border-b border-raios-border/60 text-[10px] uppercase tracking-wider text-raios-muted">
                  <th className="px-2 py-2 font-semibold">Subsistema / localização</th>
                  <th className="px-2 py-2 font-semibold">Classificação</th>
                  <th className="px-2 py-2 text-right font-semibold">Ocorrências</th>
                  <th className="px-2 py-2 font-semibold">Última falha</th>
                </tr>
              </thead>
              <tbody>
                {data.summary.map((item) => (
                  <tr key={item.fingerprint} className="border-b border-raios-border/40 align-top last:border-0">
                    <td className="px-2 py-3">
                      <p className="text-xs font-medium text-raios-text">
                        {SUBSYSTEM_LABELS[item.subsystem]} · {item.component}
                      </p>
                      <p className="mt-1 max-w-xl break-words text-[10px] text-raios-muted">
                        {item.sample_message}
                      </p>
                    </td>
                    <td className="px-2 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge
                          variant={
                            item.severity === 'critical'
                              ? 'danger'
                              : item.classification === 'other'
                                ? 'warning'
                                : 'info'
                          }
                        >
                          {ERROR_CLASSIFICATION_LABELS[item.classification]}
                        </Badge>
                        {item.error_code && (
                          <span className="font-mono text-[10px] text-raios-muted">{item.error_code}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right font-mono text-xs font-semibold text-raios-text">
                      {formatExactCount(item.occurrences)}
                    </td>
                    <td className="px-2 py-3 text-xs text-raios-muted">
                      {new Date(item.last_occurred_at).toLocaleString('pt-BR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-raios-border/60 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex items-center gap-2 text-xs text-raios-muted">
              Erros por página
              <select
                aria-label="Erros por página"
                className="rounded-md border border-raios-border bg-raios-base px-2 py-1.5 text-xs text-raios-text"
                value={summaryPageSize}
                disabled={loading}
                onChange={(event) =>
                  onSummaryPageSizeChange(Number(event.target.value) as HarnessErrorPageSize)
                }
              >
                {[10, 25, 50, 100].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>{pageSize}</option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-raios-muted">
                Página {summaryPage} de {data.summary_total_pages} · {data.summary_total_groups.toLocaleString('pt-BR')} erros
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!data.summary_has_previous || loading}
                onClick={() => onSummaryPageChange(Math.max(1, summaryPage - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!data.summary_has_next || loading}
                onClick={() => onSummaryPageChange(summaryPage + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>

          <details
            className="mt-4 rounded-lg border border-raios-border/60 bg-raios-elevated/20 p-3"
            onToggle={(event) => {
              if (
                event.currentTarget.open
                && !data.events_loaded
                && data.events_available !== false
                && !loadingEvents
              ) onLoadEvents();
            }}
          >
            <summary className="cursor-pointer text-xs font-medium text-raios-text">
              {data.events_loaded
                ? `Eventos individuais (${data.items.length} carregados)`
                : data.events_available === false
                  ? 'Eventos individuais indisponíveis'
                  : 'Eventos individuais (carregar ao abrir)'}
            </summary>
            <div className="mt-3 space-y-2">
              {loadingEvents ? (
                <Skeleton className="h-14 rounded-lg" />
              ) : data.events_available === false ? (
                <p className="text-xs text-raios-warning">
                  O resumo paginado está disponível; o detalhe dos eventos está temporariamente lento.
                </p>
              ) : !data.events_loaded ? (
                <p className="text-xs text-raios-muted">Carregando eventos sob demanda…</p>
              ) : data.items.map((item) => (
                <div
                  key={`${item.source_table}:${item.source_id}`}
                  className="rounded-md bg-raios-base/40 p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{SUBSYSTEM_LABELS[item.subsystem]}</Badge>
                    <span className="font-mono text-[10px] text-raios-muted">
                      {item.source_table}:{item.source_id}
                    </span>
                    <span className="ml-auto text-[10px] text-raios-muted">
                      {new Date(item.occurred_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                  <p className="mt-2 break-words text-xs text-raios-text">{item.error_message}</p>
                  <p className="mt-1 font-mono text-[10px] text-raios-muted">
                    trace={item.trace_id ?? '—'} · task={item.task_id ?? '—'} · demand=
                    {item.demand_id ?? '—'}
                  </p>
                </div>
              ))}
              {data.has_more && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onLoadMore}
                  loading={loadingMore}
                  disabled={loadingMore}
                >
                  Carregar mais eventos
                </Button>
              )}
            </div>
          </details>
        </>
      ) : null}
    </Card>
  );
}

function HarnessHistoryRow({
  subsystem,
  history,
  current,
}: {
  subsystem: HarnessSubsystem;
  history: HarnessHealthDay[];
  current?: HarnessCurrentHealth;
}) {
  const chronological = history;
  // O percentual principal é o agregado dos 90 dias. O badge, por contrato de
  // produto, representa exclusivamente o último dia da janela (hoje em São
  // Paulo). Nunca usar o probe/janela móvel para sobrescrever esse estado.
  const today = chronological.at(-1);
  const currentStatus = today?.status ?? 'unknown';
  const aggregate = history.reduce(
    (total, day) => ({
      events: total.events + day.total,
      successes: total.successes + day.successes,
      failures: total.failures + day.failures,
      fallbacks: total.fallbacks + day.fallbacks,
    }),
    { events: 0, successes: 0, failures: 0, fallbacks: 0 },
  );
  const aggregateSuccessRate =
    aggregate.events === 0 ? null : aggregate.successes / aggregate.events;
  return (
    <div className="grid gap-2 border-b border-raios-border/60 py-3 last:border-0 md:grid-cols-[150px_1fr_170px] md:items-center">
      <div>
        <p className="text-xs font-medium text-raios-text">{SUBSYSTEM_LABELS[subsystem]}</p>
        <p className="text-[10px] text-raios-muted">{history.length} dias na janela</p>
      </div>
      <TooltipProvider delayDuration={80}>
        <div
          className="grid h-5 gap-px"
          style={{ gridTemplateColumns: `repeat(${Math.max(chronological.length, 1)}, minmax(3px, 1fr))` }}
          aria-label={`Histórico de ${SUBSYSTEM_LABELS[subsystem]}`}
        >
          {chronological.length === 0 ? (
            <span className="rounded-sm bg-raios-elevated" title="Sem amostra" />
          ) : chronological.map((day) => (
            <Tooltip key={`${subsystem}-${day.day}`}>
              <TooltipTrigger asChild>
                <span
                  className={cn('rounded-[2px]', healthCellClass(day.status))}
                  aria-label={`${day.day}: ${day.failures} erros em ${day.total} eventos`}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="font-semibold">
                  {SUBSYSTEM_LABELS[subsystem]} · {new Date(`${day.day}T12:00:00`).toLocaleDateString('pt-BR')}
                </p>
                {day.total === 0 ? (
                  <p className="mt-1">Sem eventos neste dia.</p>
                ) : (
                  <>
                    <p className="mt-1">
                      {day.failures.toLocaleString('pt-BR')} erros / {day.total.toLocaleString('pt-BR')} eventos
                    </p>
                    <p>
                      {formatRatioAsPercent(day.success_rate)} de sucesso · {day.fallbacks.toLocaleString('pt-BR')} fallbacks
                    </p>
                  </>
                )}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>
      </TooltipProvider>
      <div className="text-left md:text-right">
        <div className="flex items-center gap-2 md:justify-end">
          <div>
            <p className="text-xs font-semibold text-raios-text">
              {formatRatioAsPercent(aggregateSuccessRate)}
            </p>
            <p className="text-[10px] text-raios-muted">
              90 dias · {aggregate.failures.toLocaleString('pt-BR')} falhas / {aggregate.events.toLocaleString('pt-BR')} eventos
            </p>
            <p className="text-[10px] text-raios-muted">
              Hoje · {formatRatioAsPercent(today?.success_rate ?? null)}
              {' · '}{today?.failures.toLocaleString('pt-BR') ?? 0} falhas / {today?.total.toLocaleString('pt-BR') ?? 0} eventos
            </p>
          </div>
          <Badge
            variant={
              currentStatus === 'operational'
                ? 'success'
                : currentStatus === 'degraded'
                  ? 'warning'
                  : currentStatus === 'outage'
                    ? 'danger'
                    : 'outline'
            }
          >
            {currentStatus}
          </Badge>
        </div>
        <p className="mt-1 text-[9px] text-raios-text-disabled">
          Rollup diário: {current?.coverage_source ?? 'none'}
        </p>
      </div>
    </div>
  );
}

/* ─── Página ──────────────────────────────────────────────────────────── */

export function HealthPage() {
  const { tenant } = useAuth();
  const isAdmin = tenant?.is_admin === true;
  const [tab, setTab] = useState<HealthTab>('system');

  const [live, setLive] = useState<{ status: string } | null>(null);
  const [ready, setReady] = useState<HealthData | null>(null);
  const [liveErr, setLiveErr] = useState('');
  const [readyErr, setReadyErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [harness, setHarness] = useState<HarnessHealthResponse | null>(null);
  const [harnessErrors, setHarnessErrors] = useState<HarnessErrorsResponse | null>(null);
  const [harnessAssets, setHarnessAssets] = useState<HarnessAssetInventory[]>([]);
  const [harnessError, setHarnessError] = useState('');
  const [harnessErrorsError, setHarnessErrorsError] = useState('');
  const [harnessErrorsLoading, setHarnessErrorsLoading] = useState(false);
  const [loadingMoreHarnessErrors, setLoadingMoreHarnessErrors] = useState(false);
  const [loadingHarnessEvents, setLoadingHarnessEvents] = useState(false);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState<HarnessErrorPageSize>(25);

  const refreshSystem = useCallback(async () => {
    setLoading(true);
    setLiveErr('');
    setReadyErr('');
    setError(null);
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    const [liveResult, readyResult] = await Promise.allSettled([
      api.healthLive(),
      api.healthReady(),
    ]);

    if (liveResult.status === 'fulfilled') setLive(liveResult.value);
    else {
      const msg = liveResult.reason instanceof Error ? liveResult.reason.message : 'Erro';
      setLiveErr(msg);
      setError(msg);
    }
    if (readyResult.status === 'fulfilled') setReady(readyResult.value);
    else {
      const msg = readyResult.reason instanceof Error ? readyResult.reason.message : 'Erro';
      setReadyErr(msg);
      setError(msg);
    }
    setLastCheck(new Date());
    setLoading(false);
  }, [isAdmin]);

  const loadHarnessErrorPage = useCallback(async (
    page: number,
    pageSize: HarnessErrorPageSize,
  ) => {
    setHarnessErrorsLoading(true);
    setHarnessErrorsError('');
    try {
      const response = await api.getHarnessErrors({
        hours: 48,
        limit: 50,
        summaryPage: page,
        summaryLimit: pageSize,
      });
      setHarnessErrors(response);
      setSummaryPage(response.summary_page);
      setSummaryPageSize(response.summary_page_size);
    } catch (loadError) {
      setHarnessErrorsError(
        loadError instanceof Error ? loadError.message : 'Erro ao carregar os erros',
      );
    } finally {
      setHarnessErrorsLoading(false);
    }
  }, []);

  const refreshHarness = useCallback(async (fresh = false, includeAssets = true) => {
    if (!isAdmin) return;
    setLoading(true);
    setHarnessError('');

    // As duas leituras mais caras são intencionalmente sequenciais. A página
    // antiga disparava cinco consultas privadas juntas e criava um pico contra
    // o Postgres justamente quando o Health era usado para investigar pressão.
    try {
      setHarness(await api.getHarnessHealth(90, fresh));
    } catch (loadError) {
      setHarnessError(loadError instanceof Error ? loadError.message : 'Erro');
    }

    await loadHarnessErrorPage(summaryPage, summaryPageSize);
    setLastCheck(new Date());
    setLoading(false);

    if (!includeAssets) return;
    try {
      setHarnessAssets(await Promise.all([
        api.listHarnessAssets('agent'),
        api.listHarnessAssets('skill'),
        api.listHarnessAssets('loop'),
      ]));
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : 'Erro';
      setHarnessError((current) => current || message);
    }
  }, [isAdmin, loadHarnessErrorPage, summaryPage, summaryPageSize]);

  const loadMoreHarnessErrors = useCallback(async () => {
    if (!harnessErrors?.next_cursor || loadingMoreHarnessErrors) return;
    setLoadingMoreHarnessErrors(true);
    setHarnessErrorsError('');
    try {
      const next = await api.getHarnessErrors({
        hours: 48,
        limit: 50,
        summaryPage,
        summaryLimit: summaryPageSize,
        includeEvents: true,
        cursor: harnessErrors.next_cursor,
      });
      setHarnessErrors((current) =>
        current
          ? {
              ...next,
              items: [...current.items, ...next.items],
            }
          : next,
      );
    } catch (loadError) {
      setHarnessErrorsError(loadError instanceof Error ? loadError.message : 'Erro ao carregar mais eventos');
    } finally {
      setLoadingMoreHarnessErrors(false);
    }
  }, [harnessErrors, loadingMoreHarnessErrors, summaryPage, summaryPageSize]);

  const loadHarnessEvents = useCallback(async () => {
    if (!harnessErrors || harnessErrors.events_loaded || loadingHarnessEvents) return;
    setLoadingHarnessEvents(true);
    setHarnessErrorsError('');
    try {
      const response = await api.getHarnessErrors({
        hours: 48,
        limit: 50,
        summaryPage: harnessErrors.summary_page,
        summaryLimit: harnessErrors.summary_page_size,
        includeEvents: true,
      });
      setHarnessErrors(response);
    } catch (loadError) {
      setHarnessErrorsError(
        loadError instanceof Error ? loadError.message : 'Erro ao carregar eventos individuais',
      );
    } finally {
      setLoadingHarnessEvents(false);
    }
  }, [harnessErrors, loadingHarnessEvents]);

  const changeSummaryPage = useCallback((page: number) => {
    void loadHarnessErrorPage(page, summaryPageSize);
  }, [loadHarnessErrorPage, summaryPageSize]);

  const changeSummaryPageSize = useCallback((pageSize: HarnessErrorPageSize) => {
    void loadHarnessErrorPage(1, pageSize);
  }, [loadHarnessErrorPage]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await refreshSystem();
    })();
    return () => { cancelled = true; };
  }, [refreshSystem]);

  // Consultas privadas e pesadas só começam quando o admin abre o Harness.
  useEffect(() => {
    if (tab !== 'harness' || !isAdmin || harness !== null) return;
    void refreshHarness();
  }, [tab, isAdmin, harness, refreshHarness]);

  // Auto-refresh 60s nas superfícies operacionais privadas.
  useEffect(() => {
    if (tab === 'system' || !isAdmin) return;
    const timer = setInterval(() => {
      if (tab === 'harness') void refreshHarness(false, false);
      else void refreshSystem();
    }, 60_000);
    return () => clearInterval(timer);
  }, [tab, refreshHarness, refreshSystem, isAdmin]);

  const refreshActiveTab = useCallback(() => {
    if (tab === 'harness') void refreshHarness(true);
    else void refreshSystem();
  }, [tab, refreshHarness, refreshSystem]);

  const overallStatus = liveErr || readyErr ? 'error' : (live?.status === 'ok' && ready?.status === 'ok') ? 'ok' : 'degraded';

  if (!isAdmin) {
    return (
      <div>
        <PageHeader
          title="Health"
          description="Observabilidade operacional privada do RAIOS"
          className="mb-4"
        />
        <Card className="p-8 text-center">
          <LockKeyhole className="mx-auto mb-3 h-6 w-6 text-raios-muted" />
          <h2 className="text-sm font-semibold text-raios-text">Área exclusiva para admins</h2>
          <p className="mt-2 text-xs text-raios-muted">
            Métricas, incidentes e ativos do Harness não são expostos para tenants comuns.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Health"
        description={`Status do sistema${lastCheck ? ` | Última verificação: ${lastCheck.toLocaleTimeString('pt-BR')}` : ''}`}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={refreshActiveTab}
            loading={loading}
            disabled={loading}
          >
            <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
            {loading ? 'Verificando...' : 'Atualizar'}
          </Button>
        }
        className="mb-4"
      />

      <Tabs
        items={[
          { value: 'system', label: 'Sistema', icon: HeartPulse },
          { value: 'operational', label: 'Operacional', icon: Activity },
          { value: 'harness', label: 'Harness · 90 dias', icon: ShieldCheck },
        ]}
        value={tab}
        onChange={(v) => setTab(v as HealthTab)}
        className="mb-6"
      />

      {tab === 'system' && (
        <>
          {/* Overall status */}
          <Card className={cn(
            'p-6 mb-6',
            overallStatus === 'ok'
              ? 'bg-raios-success/5'
              : overallStatus === 'degraded'
              ? 'bg-raios-warning/5'
              : 'bg-raios-danger/5'
          )}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-raios-muted mb-2">Status Geral</p>
                <Badge variant={getStatusBadgeVariant(overallStatus)}>
                  {overallStatus.toUpperCase()}
                </Badge>
              </div>
              {ready && (
                <div className="w-full sm:w-64">
                  <ProgressBar
                    value={Math.min((ready.latency_ms / 500) * 100, 100)}
                    variant={getLatencyVariant(ready.latency_ms)}
                    size="sm"
                    label={`${ready.latency_ms}ms`}
                  />
                </div>
              )}
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Liveness */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-raios-text-secondary">Liveness</h2>
                  <p className="text-[10px] text-raios-text-disabled font-mono mt-0.5">/health/live</p>
                </div>
                {live && <Badge variant={getStatusBadgeVariant(live.status)}>{live.status.toUpperCase()}</Badge>}
              </div>
              {liveErr ? (
                <Card className="bg-raios-danger/10  px-3 py-2">
                  <p className="text-raios-danger-text text-xs">{liveErr}</p>
                </Card>
              ) : !live ? (
                <Skeleton className="h-8 rounded-lg" />
              ) : (
                <p className="text-xs text-raios-muted">O serviço está respondendo a requests.</p>
              )}
            </Card>

            {/* Readiness */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-raios-text-secondary">Readiness</h2>
                  <p className="text-[10px] text-raios-text-disabled font-mono mt-0.5">/health/ready</p>
                </div>
                {ready && <Badge variant={getStatusBadgeVariant(ready.status)}>{ready.status.toUpperCase()}</Badge>}
              </div>
              {readyErr ? (
                <Card className="bg-raios-danger/10  px-3 py-2">
                  <p className="text-raios-danger-text text-xs">{readyErr}</p>
                </Card>
              ) : !ready ? (
                <Skeleton className="h-24 rounded-lg" />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-raios-elevated/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Database className={cn(
                        'w-4 h-4',
                        ready.checks.database === 'ok' ? 'text-raios-success' : 'text-raios-danger'
                      )} />
                      <span className="text-sm text-raios-text-secondary">Database</span>
                    </div>
                    <Badge variant={getStatusBadgeVariant(ready.checks.database)}>
                      {ready.checks.database.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-raios-elevated/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Boxes className={cn(
                        'w-4 h-4',
                        ready.checks.pgvector === 'ok' ? 'text-raios-success' : 'text-raios-danger'
                      )} />
                      <span className="text-sm text-raios-text-secondary">pgvector</span>
                    </div>
                    <Badge variant={getStatusBadgeVariant(ready.checks.pgvector)}>
                      {ready.checks.pgvector.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="pt-2">
                    <ProgressBar
                      value={Math.min((ready.latency_ms / 500) * 100, 100)}
                      variant={getLatencyVariant(ready.latency_ms)}
                      label={`Latência: ${ready.latency_ms}ms`}
                    />
                  </div>
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {tab === 'operational' && (
        <div className="space-y-6">
          {error && (
            <div className="ghost-card p-4 flex items-center gap-3 border border-raios-danger/30 bg-raios-danger/5">
              <AlertTriangle className="h-4 w-4 text-raios-danger shrink-0" />
              <span className="text-sm text-raios-danger">{error}</span>
            </div>
          )}

          {!live && !ready && !error && (
            <div className="ghost-card p-8 text-center">
              <RefreshCw className="h-5 w-5 text-raios-muted animate-spin mx-auto mb-2" />
              <p className="text-sm text-raios-muted">Verificando saúde do sistema...</p>
            </div>
          )}

          {(live || ready) && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <HealthCard
                icon={Activity}
                title="Gateway OpenClaw"
                status={live?.status === 'ok' ? 'online' : live ? 'offline' : 'checking'}
              >
                <MetaRow label="Status" value={live?.status ?? '-'} />
                <MetaRow label="Latência" value={ready ? latencyLabel(ready.latency_ms) : '-'} />
              </HealthCard>

              <HealthCard
                icon={Database}
                title="Banco de Dados"
                status={ready?.checks.database === 'ok' ? 'online' : ready ? 'offline' : 'checking'}
              >
                <MetaRow label="Database" value={ready?.checks.database ?? '-'} />
                <MetaRow label="pgvector" value={ready?.checks.pgvector ?? '-'} />
                <MetaRow label="Provider" value="Supabase" />
              </HealthCard>

              {/* Workspace — no backend endpoint yet (preservado de MCHealthPage) */}
              <HealthCard icon={HardDrive} title="Workspace" status="checking">
                <p className="text-xs text-raios-muted">Em breve</p>
              </HealthCard>

              {/* LLM Usage — no backend endpoint yet (preservado de MCHealthPage) */}
              <HealthCard icon={Cpu} title="LLM (últimos 7 dias)" status="checking">
                <p className="text-xs text-raios-muted">Em breve</p>
              </HealthCard>
            </div>
          )}

          <div className="flex items-center gap-2 text-2xs text-raios-muted">
            <Badge variant="outline">
              <RefreshCw className="h-3 w-3 mr-1" />
              Auto-refresh ativo (60s)
            </Badge>
          </div>
        </div>
      )}

      {tab === 'harness' && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="warning">
              <LockKeyhole className="mr-1 h-3 w-3" />
              Privado · admin-only
            </Badge>
            <Badge variant="outline">Janela fixa: 90 dias</Badge>
            {harness?.generated_at && (
              <Badge variant="outline">
                Gerado {new Date(harness.generated_at).toLocaleString('pt-BR')}
              </Badge>
            )}
          </div>

          {harnessError && (
            <div className="ghost-card flex items-center gap-3 border border-raios-danger/30 bg-raios-danger/5 p-4">
              <AlertTriangle className="h-4 w-4 shrink-0 text-raios-danger" />
              <span className="text-sm text-raios-danger">{harnessError}</span>
            </div>
          )}

          {!harness && loading && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton key={index} className="h-24 rounded-lg" />
              ))}
            </div>
          )}

          {harness && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <Card className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-raios-muted">Chamadas lógicas</p>
                  <p className="mt-2 text-2xl font-semibold text-raios-text">
                    {harness.llm_resilience.logical_calls.toLocaleString('pt-BR')}
                  </p>
                  <p className="mt-1 text-xs text-raios-muted">
                    {harness.llm_resilience.successful_calls.toLocaleString('pt-BR')} concluídas
                  </p>
                </Card>
                <Card className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-raios-muted">Primeira chamada</p>
                  <p className="mt-2 text-2xl font-semibold text-raios-text">
                    {harness.llm_resilience.consecutive_first_attempt_successes.toLocaleString('pt-BR')}
                  </p>
                  <p className="mt-1 text-xs text-raios-muted">
                    streak atual · recorde{' '}
                    {(harness.llm_resilience.max_consecutive_first_attempt_successes ?? 0).toLocaleString('pt-BR')}
                  </p>
                  <p className="mt-1 text-[10px] text-raios-muted">
                    {harness.llm_resilience.max_consecutive_first_attempt_successes_at
                      ? `Recorde em ${new Date(harness.llm_resilience.max_consecutive_first_attempt_successes_at).toLocaleString('pt-BR')}`
                      : 'Recorde ainda sem amostra'}
                  </p>
                </Card>
                <Card className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-raios-muted">Taxa de fallback</p>
                  <p className="mt-2 text-2xl font-semibold text-raios-text">
                    {harness.llm_resilience.fallback_rate == null
                      ? '—'
                      : formatRatioAsPercent(harness.llm_resilience.fallback_rate)}
                  </p>
                  <p className="mt-1 text-xs text-raios-muted">
                    {harness.llm_resilience.fallback_calls.toLocaleString('pt-BR')} chamadas
                  </p>
                </Card>
                <Card className="p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-raios-muted">Rotas esgotadas</p>
                  <p className="mt-2 text-2xl font-semibold text-raios-text">
                    {harness.llm_resilience.exhausted_calls.toLocaleString('pt-BR')}
                  </p>
                  <p className="mt-1 text-xs text-raios-muted">
                    {harness.llm_resilience.exhaustion_rate == null
                      ? 'sem amostra'
                      : `${formatRatioAsPercent(harness.llm_resilience.exhaustion_rate)} das chamadas`}
                  </p>
                  <p className="mt-1 text-[10px] text-raios-muted">
                    {harness.llm_resilience.latest_logical_call_at
                      ? `Ledger até ${new Date(harness.llm_resilience.latest_logical_call_at).toLocaleString('pt-BR')}`
                      : 'Ledger sem chamadas'}
                  </p>
                </Card>
              </div>

              <Card className="p-5">
                <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-raios-text">Saúde por subsistema</h2>
                    <p className="text-xs text-raios-muted">
                      Cada célula representa um dia. Verde operacional, amarelo degradado, vermelho indisponível.
                    </p>
                  </div>
                  <Badge variant="outline">{SUBSYSTEMS.length} subsistemas</Badge>
                </div>
                <div>
                  {SUBSYSTEMS.map((subsystem) => (
                    <HarnessHistoryRow
                      key={subsystem}
                      subsystem={subsystem}
                      history={harness.subsystems[subsystem] ?? []}
                      current={harness.current?.find((item) => item.subsystem === subsystem)}
                    />
                  ))}
                </div>
              </Card>

              <HarnessErrorsPanel
                data={harnessErrors}
                error={harnessErrorsError}
                loading={harnessErrorsLoading}
                loadingMore={loadingMoreHarnessErrors}
                loadingEvents={loadingHarnessEvents}
                onLoadMore={loadMoreHarnessErrors}
                onLoadEvents={loadHarnessEvents}
                summaryPage={summaryPage}
                summaryPageSize={summaryPageSize}
                onSummaryPageChange={changeSummaryPage}
                onSummaryPageSizeChange={changeSummaryPageSize}
              />

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card className="p-5">
                  <div className="mb-4">
                    <h2 className="text-sm font-semibold text-raios-text">Camadas operacionais</h2>
                    <p className="text-xs text-raios-muted">
                      Inventário privado. Harness não aparece nas listagens de aplicação.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {harnessAssets.map((inventory) => {
                      const internal = inventory.items.filter((item) => item.requires_admin).length;
                      const application = inventory.items.length - internal;
                      return (
                        <div
                          key={inventory.kind}
                          className="flex items-center justify-between rounded-lg bg-raios-elevated/40 p-3"
                        >
                          <div>
                            <p className="text-sm font-medium capitalize text-raios-text">
                              {inventory.kind === 'agent' ? 'Agentes' : inventory.kind === 'skill' ? 'Skills' : 'Loops'}
                            </p>
                            <p className="text-[10px] text-raios-muted">
                              {internal} Harness · {application} aplicação
                            </p>
                          </div>
                          <Badge variant={internal > 0 ? 'warning' : 'outline'}>
                            {inventory.items.length} total
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                <Card className="p-5">
                  <div className="mb-4">
                    <h2 className="text-sm font-semibold text-raios-text">Mudanças e experimentos</h2>
                    <p className="text-xs text-raios-muted">
                      Eventos correlacionáveis com a evolução das métricas.
                    </p>
                  </div>
                  {harness.changes.length === 0 ? (
                    <p className="rounded-lg bg-raios-elevated/40 p-4 text-xs text-raios-muted">
                      Nenhuma mudança registrada nesta janela.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {harness.changes.slice(0, 12).map((change) => (
                        <div key={change.id} className="border-b border-raios-border/60 pb-3 last:border-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline">{SUBSYSTEM_LABELS[change.subsystem]}</Badge>
                            <Badge variant="info">{change.event_type}</Badge>
                            <span className="text-[10px] text-raios-muted">
                              {new Date(change.occurred_at).toLocaleString('pt-BR')}
                            </span>
                          </div>
                          <p className="mt-2 text-sm font-medium text-raios-text">{change.title}</p>
                          {change.description && (
                            <p className="mt-1 text-xs text-raios-muted">{change.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}

          <div className="flex items-center gap-2 text-2xs text-raios-muted">
            <Badge variant="outline">
              <RefreshCw className="mr-1 h-3 w-3" />
              Auto-refresh ativo (60s)
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
}
